// ── DB 관리 — 행 편집 모달 컴포넌트 ──────────────────────────────────────────
// 선택한 행의 편집 가능 컬럼을 폼으로 수정
import React, { useState } from 'react';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../../../hooks/useOverlayClickClose';
import {
  modalOverlayStyle, modalBoxStyle, modalHeaderStyle, modalCloseBtnStyle,
  modalHeaderLeftStyle, modalHeaderIconBoxStyle, modalHeaderTitleStyle, modalHeaderSubStyle,
  readonlyChipStyle, fieldLabelStyle, fieldInputStyle, textareaStyle,
  modalFooterStyle, cancelBtnStyle, saveBtnStyle,
} from './styles';

/**
 * 행 편집 모달
 * @param {object}   row       — 편집 중인 행 데이터
 * @param {object[]} columns   — 컬럼 메타 배열
 * @param {string}   pkColumn  — PK 컬럼명
 * @param {string}   tableName — 현재 테이블명 (헤더 표시용)
 * @param {Function} onClose   — 모달 닫기 콜백
 * @param {Function} onSave    — 저장 콜백 (pkValue, updates) => Promise<string|null>
 *                               (null 반환 시 성공, 문자열 반환 시 오류 메시지)
 */
export function RowEditModal({ row, columns, pkColumn, tableName, onClose, onSave }) {
  useBodyScrollLock(true);
  const overlayCloseProps = useOverlayClickClose(onClose);
  // 편집 가능 컬럼: PK 및 민감 컬럼 제외
  const editableCols = columns.filter((c) => c.column_name !== pkColumn && !c.is_sensitive);
  const pkValue = row[pkColumn];

  // 폼 초기값: null → 빈 문자열, 객체 → JSON 문자열
  const initForm = () => {
    const f = {};
    for (const col of editableCols) {
      const v = row[col.column_name];
      if (v === null || v === undefined) f[col.column_name] = '';
      else if (typeof v === 'object')    f[col.column_name] = JSON.stringify(v, null, 2);
      else                               f[col.column_name] = String(v);
    }
    return f;
  };

  const [originalForm]      = useState(initForm);
  const [form, setForm]     = useState(initForm);
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  function setValue(colName, value) {
    setForm((prev) => ({ ...prev, [colName]: value }));
  }

  async function handleSave() {
    setSaving(true);
    setErrMsg('');
    // 빈 문자열 또는 undefined → null로 변환하고, 바뀐 컬럼만 전송한다.
    const updates = {};
    for (const col of editableCols) {
      const v = form[col.column_name];
      const before = originalForm[col.column_name] ?? '';
      const current = v ?? '';
      if (current !== before) {
        updates[col.column_name] = (v === '' || v === undefined) ? null : v;
      }
    }
    if (Object.keys(updates).length === 0) {
      setSaving(false);
      setErrMsg('변경된 내용이 없습니다.');
      return;
    }
    const err = await onSave(pkValue, updates);
    setSaving(false);
    if (err) setErrMsg(err);
  }

  // JSON/JSONB 또는 긴 텍스트 컬럼은 textarea, 나머지는 input
  function isMultiline(col) {
    const t = col.data_type?.toLowerCase() || '';
    return t === 'jsonb' || t === 'json' || t === 'text';
  }

  return (
    <div style={modalOverlayStyle} {...overlayCloseProps}>
      <div style={modalBoxStyle} onClick={(e) => e.stopPropagation()}>

        {/* 헤더: 테이블명 + PK 값 + 닫기 버튼 — 디자인 표준 검정 밴드 */}
        <div style={modalHeaderStyle}>
          <div style={modalHeaderLeftStyle}>
            <div style={modalHeaderIconBoxStyle}>✏️</div>
            <div style={{ minWidth: 0 }}>
              <div style={modalHeaderTitleStyle}>{tableName}</div>
              <div style={modalHeaderSubStyle}>
                {pkColumn}: <strong>{String(pkValue)}</strong>
              </div>
            </div>
          </div>
          <button style={modalCloseBtnStyle} onClick={onClose}>✕</button>
        </div>

        {/* 읽기 전용 정보: PK + 민감 컬럼 칩 */}
        {columns.filter((c) => c.column_name === pkColumn || c.is_sensitive).length > 0 && (
          <div style={{ padding: '8px 20px 0', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {columns
              .filter((c) => c.column_name === pkColumn || c.is_sensitive)
              .map((col) => (
                <div key={col.column_name} style={readonlyChipStyle}>
                  <span style={{ color: '#7c3aed', fontWeight: 700 }}>{col.column_name}</span>
                  <span style={{ marginLeft: 4, color: 'var(--text-muted)' }}>
                    {col.is_sensitive ? '●●●' : String(row[col.column_name] ?? 'null')}
                  </span>
                  <span style={{ marginLeft: 4, fontSize: 10, color: '#d97706' }}>
                    {col.is_sensitive ? '🔒' : 'PK'}
                  </span>
                </div>
              ))}
          </div>
        )}

        {/* 편집 폼 */}
        <div style={{ padding: '12px 20px', overflowY: 'auto', flex: 1, display: 'grid', gap: 10 }}>
          {editableCols.map((col) => (
            <div key={col.column_name}>
              <label style={fieldLabelStyle}>
                {col.column_name}
                <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{col.data_type}</span>
              </label>
              {isMultiline(col) ? (
                <textarea
                  style={textareaStyle}
                  value={form[col.column_name]}
                  onChange={(e) => setValue(col.column_name, e.target.value)}
                  placeholder="null"
                  rows={4}
                />
              ) : (
                <input
                  style={fieldInputStyle}
                  value={form[col.column_name]}
                  onChange={(e) => setValue(col.column_name, e.target.value)}
                  placeholder="null"
                />
              )}
            </div>
          ))}
          {editableCols.length === 0 && (
            <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 24 }}>수정 가능한 컬럼이 없습니다.</div>
          )}
        </div>

        {/* 오류 메시지 */}
        {errMsg && <div style={{ padding: '0 20px 8px', color: '#dc2626', fontSize: 12 }}>{errMsg}</div>}

        {/* 푸터: 취소 + 저장 버튼 */}
        <div style={modalFooterStyle}>
          <button style={cancelBtnStyle} onClick={onClose}>취소</button>
          <button
            style={saveBtnStyle}
            disabled={saving || editableCols.length === 0}
            onClick={handleSave}
          >
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
